/**
 * 🎠 CAROUSEL SERVICE - Manage dashboard carousels
 */

import { getApiBaseUrl } from '@/config/environment';


export interface FilterCriteria {
  location?: string;
  transmission?: string;
  make?: string;
  category?: string;
  year?: {
    operator: '>' | '>=' | '<' | '<=' | '=';
    value: number;
  };
  daily_rate?: {
    operator: '>' | '>=' | '<' | '<=' | '=';
    value: number;
  };
  [key: string]: any; // Allow flexible filters
}

export interface Carousel {
  id: string;
  title: string;
  subtitle: string | null;
  filter_criteria: FilterCriteria;
  max_vehicles: number;
  display_order: number;
  card_style: string;
  is_active: boolean;
  total_views: number;
  total_clicks: number;
  vehicle_count?: number; // Number of vehicles matching the filter (from backend)
  created_at: string;
  updated_at: string;
}

export interface CreateCarouselData {
  title: string;
  subtitle?: string;
  filterCriteria: FilterCriteria;  // Backend expects camelCase
  maxVehicles?: number;            // Backend expects camelCase
  displayOrder?: number;           // Backend expects camelCase
  cardStyle?: string;              // Backend expects camelCase
}

export interface UpdateCarouselData {
  title?: string;
  subtitle?: string;
  filterCriteria?: FilterCriteria;  // Backend expects camelCase
  maxVehicles?: number;             // Backend expects camelCase
  displayOrder?: number;            // Backend expects camelCase
  cardStyle?: string;               // Backend expects camelCase
  isActive?: boolean;               // Backend expects camelCase
}

export interface CarouselsResponse {
  success: boolean;
  data: {
    carousels: Carousel[];
    total: number;
  };
  error?: string;
}

export interface CarouselResponse {
  success: boolean;
  data?: Carousel;
  message?: string;
  error?: string;
}

export const carouselService = {
  /**
   * Get all carousels
   */
  async getCarousels(): Promise<CarouselsResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/carousels`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API Error ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching carousels:', error);
      return {
        success: false,
        data: { carousels: [], total: 0 },
        error: error instanceof Error ? error.message : 'Failed to fetch carousels'
      };
    }
  },

  /**
   * Get single carousel by ID
   */
  async getCarousel(carouselId: string): Promise<CarouselResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/carousels/${carouselId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API Error ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching carousel:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch carousel'
      };
    }
  },

  /**
   * Create new carousel
   */
  async createCarousel(data: CreateCarouselData): Promise<CarouselResponse> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/carousels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API Error ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error creating carousel:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create carousel'
      };
    }
  },

  /**
   * Update carousel
   */
  async updateCarousel(carouselId: string, data: UpdateCarouselData): Promise<CarouselResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/carousels/${carouselId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API Error ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error updating carousel:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update carousel'
      };
    }
  },

  /**
   * Delete carousel
   */
  async deleteCarousel(carouselId: string): Promise<CarouselResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/carousels/${carouselId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API Error ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error deleting carousel:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete carousel'
      };
    }
  }
};
